import { CardContent, FormLayout, Button } from 'shared-components';
import {
  useGetBillingStatusQuery,
  useSetupBillingMutation,
  useCreateBillingPortalMutation,
} from './billing.api';
import { useState } from 'react';

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  active: { label: 'Active', color: '#16a34a' },
  trialing: { label: 'Trial', color: '#2563eb' },
  past_due: { label: 'Past Due', color: '#dc2626' },
  canceled: { label: 'Canceled', color: '#6b7280' },
  free: { label: 'Free', color: '#8b5cf6' },
  none: { label: 'Not configured', color: '#9ca3af' },
};

export const SettingsBilling = () => {
  const { data: billing, isLoading } = useGetBillingStatusQuery();
  const [setupBilling, { isLoading: isSettingUp }] = useSetupBillingMutation();
  const [createPortal] = useCreateBillingPortalMutation();
  const [message, setMessage] = useState<string | null>(null);

  if (isLoading) {
    return (
      <CardContent>
        <div>Loading billing status...</div>
      </CardContent>
    );
  }

  const status = STATUS_LABELS[billing?.billingStatus ?? 'none'] ?? STATUS_LABELS.none;
  const pricing = billing?.resolvedPricing;

  const handleSetup = async () => {
    setMessage(null);
    try {
      const baseUrl = window.location.origin + window.location.pathname;
      const result = await setupBilling({
        successUrl: baseUrl + '?billing=success',
        cancelUrl: baseUrl + '?billing=canceled',
      }).unwrap();
      window.location.href = result.checkoutUrl;
    } catch {
      setMessage('Failed to start billing setup. Please try again.');
    }
  };

  const handlePortal = async () => {
    setMessage(null);
    try {
      const result = await createPortal({
        returnUrl: window.location.href,
      }).unwrap();
      window.open(result.portalUrl, '_blank');
    } catch {
      setMessage('Failed to open billing portal.');
    }
  };

  return (
    <CardContent>
      <FormLayout>
        <h2 style={{ margin: '0 0 1rem' }}>Billing</h2>

        {/* Status banner */}
        <div
          style={{
            padding: '1rem',
            borderRadius: '0.5rem',
            backgroundColor: '#f9fafb',
            border: '1px solid #e5e7eb',
            marginBottom: '1.5rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <span style={{ fontWeight: 600, fontSize: '1rem' }}>Status:</span>
            <span
              style={{
                color: status.color,
                fontWeight: 600,
                fontSize: '1rem',
              }}
            >
              {status.label}
            </span>
          </div>

          {billing?.pricingTier && (
            <div style={{ marginBottom: '0.5rem' }}>
              <span style={{ fontWeight: 600 }}>Plan: </span>
              <span>{billing.pricingTier.charAt(0).toUpperCase() + billing.pricingTier.slice(1)}</span>
            </div>
          )}

          {pricing && !pricing.isFree && (
            <div style={{ marginBottom: '0.5rem' }}>
              <span style={{ fontWeight: 600 }}>Monthly fee: </span>
              <span>
                &pound;{(pricing.monthlyFeeCents / 100).toFixed(2)}/month
              </span>
              {pricing.transactionFeeBps > 0 && (
                <span>
                  {' '}+ {(pricing.transactionFeeBps / 100).toFixed(2)}% per
                  transaction
                </span>
              )}
            </div>
          )}

          {pricing?.isFree && (
            <div style={{ marginBottom: '0.5rem' }}>
              <span style={{ fontWeight: 600 }}>Plan: </span>
              <span>Free (no subscription required)</span>
            </div>
          )}
        </div>

        {/* Past-due warning */}
        {billing?.billingStatus === 'past_due' && (
          <div
            style={{
              padding: '1rem',
              borderRadius: '0.5rem',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              marginBottom: '1.5rem',
              color: '#991b1b',
            }}
          >
            <strong>Payment failed.</strong> Please update your payment method
            to avoid service interruption.
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {!billing?.hasSubscription &&
            billing?.billingStatus !== 'free' &&
            pricing &&
            !pricing.isFree && (
              <Button type="button" disabled={isSettingUp} onClick={handleSetup}>
                {isSettingUp ? 'Setting up...' : 'Set Up Billing'}
              </Button>
            )}

          {billing?.hasCustomer && (
            <Button type="button" onClick={handlePortal}>
              Manage Billing
            </Button>
          )}

          {message && <span>{message}</span>}
        </div>
      </FormLayout>
    </CardContent>
  );
};
